import { EventEmitter } from "@angular/core";
import { CellularDevice } from "@app/cellular/models/cellular-device.model";
import { Project } from "@app/project/model";
import { AlulaDevice } from "@app/project/model/alula-device";
import { ClareServicePlan } from "@app/project/model/clare-service-plan";
import { ClareServiceSubscription } from "@app/project/model/clare-service-subscription";
import { ApiResultState } from "@app/root-store/api-result.state";
import { ClareDbCollection } from "@app/shared/resources/resource-objectifier";
import { Action } from "@ngrx/store";
import { Observable } from "rxjs";
import { DataAction, DataErrorAction, DataSuccessAction } from "../../../shared/resources/data-actions";
import { DataServiceError } from "../../../shared/services/data-service-error";
import { CreateSubscriptionPayload } from "../services/subscription-db.service";

export enum ActionTypes {
    ClearActiveSubscription = "[ClareSubscription Management] ClearActiveSubscription",
    ClearSubscriptions = "[ClareSubscription Management] ClearSubscriptions",
    CreateSubscription = "[ClareSubscription Management] CreateSubscription",
    CreateSubscriptionFailure = "[ClareSubscription Management] CreateSubscriptionFailure",
    CreateSubscriptionSuccess = "[ClareSubscription Management] CreateSubscriptionSuccess",
    GetActiveSubscriptionByProjectId = "[ClareSubscription Management] GetActiveSubscriptionByProjectId",
    GetActiveSubscriptionByProjectIdFailure = "[ClareSubscription Management] GetActiveSubscriptionByProjectIdFailure",
    GetActiveSubscriptionByProjectIdSuccess = "[ClareSubscription Management] GetActiveSubscriptionByProjectIdSuccess",
    UpdateSubscription = "[ClareSubscription Management] UpdateSubscription",
    UpdateSubscriptionFailure = "[ClareSubscription Management] UpdateSubscriptionFailure",
    UpdateSubscriptionSuccess = "[ClareSubscription Management] UpdateSubscriptionSuccess",
    ConvertSubscription = "[ClareSubscription Management] ConvertSubscription",
    ConvertSubscriptionSuccess = "[ClareSubscription Management] ConvertSubscriptionSuccess",
}

export type SubscriptionAction = DataAction<ClareServiceSubscription>;
export type SubscriptionSuccessAction = DataSuccessAction<ClareServiceSubscription | ClareDbCollection<ClareServiceSubscription>>;
type SubscriptionFailureAction = DataErrorAction<ClareServiceSubscription>;
type SubscriptionError = DataServiceError<ClareServiceSubscription>;

export class ClearActiveSubscription implements Action {
    readonly type = ActionTypes.ClearActiveSubscription;
}

export class ClearSubscriptions implements Action {
    readonly type = ActionTypes.ClearSubscriptions;
}

export class GetActiveSubscriptionByProjectId implements Action {
    readonly type = ActionTypes.GetActiveSubscriptionByProjectId;
    constructor(public readonly payload: number, public emitter?: EventEmitter<Action>) {}
}

export class GetActiveSubscriptionByProjectIdFailure implements SubscriptionFailureAction {
    readonly type = ActionTypes.GetActiveSubscriptionByProjectIdFailure;
    constructor(public readonly error: DataServiceError<any>) {}
}

export class GetActiveSubscriptionByProjectIdSuccess implements SubscriptionSuccessAction {
    readonly type = ActionTypes.GetActiveSubscriptionByProjectIdSuccess;
    constructor(public readonly result: ClareServiceSubscription) {}
}

export class CreateSubscription implements Action {
    readonly type = ActionTypes.CreateSubscription;
    constructor(
        public readonly payload: CreateSubscriptionPayload,
        public readonly uuid: string,
        public readonly newServicePlan: ClareServicePlan,
        public readonly project: Project,
        public readonly additionalFeatures?,
        public readonly isProjectOffline?,
        public readonly emitter?: EventEmitter<Action>
    ) {}
}

export class CreateSubscriptionFailure implements SubscriptionFailureAction {
    readonly type = ActionTypes.CreateSubscriptionFailure;
    constructor(public readonly error: SubscriptionError) {}
}

export class CreateSubscriptionSuccess implements SubscriptionSuccessAction {
    readonly type = ActionTypes.CreateSubscriptionSuccess;
    constructor(
        public readonly result: {
            subscription: ClareServiceSubscription;
            uuid: string;
            newServicePlan: ClareServicePlan;
            additionalFeatures?;
            isProjectOffline?: boolean;
        }
    ) {}
}

export class UpdateSubscription implements SubscriptionAction {
    readonly type = ActionTypes.UpdateSubscription;
    constructor(
        public readonly payload: ClareServiceSubscription,
        public readonly uuid: string,
        public readonly newServicePlan: ClareServicePlan,
        public readonly project: Project,
        public readonly additionalFeatures?,
        public readonly isProjectOffline?,
        public readonly emitter?: EventEmitter<Action>
    ) {}
}

export class UpdateSubscriptionFailure implements SubscriptionFailureAction {
    readonly type = ActionTypes.UpdateSubscriptionFailure;
    constructor(public readonly error: SubscriptionError) {}
}

export class UpdateSubscriptionSuccess implements SubscriptionSuccessAction {
    readonly type = ActionTypes.UpdateSubscriptionSuccess;
    constructor(
        public readonly result: {
            subscription: ClareServiceSubscription;
            uuid: string;
            newServicePlan: ClareServicePlan;
            additionalFeatures?;
            isProjectOffline?: boolean;
        }
    ) {}
}

export class ConvertSubscription implements Action {
    readonly type = ActionTypes.ConvertSubscription;

    constructor(
        public readonly payload: {
            currentSubscription: ClareServiceSubscription;
            currentServicePlan: ClareServicePlan;
            newSubscription: CreateSubscriptionPayload;
            newServicePlan: ClareServicePlan;
            alulaDevice: Observable<ApiResultState<AlulaDevice>>;
            uuid: string;
            project: Project;
            cellularDevice?: CellularDevice;
            additionalFeatures?;
            isProjectOffline?: boolean;
        },
        public onSuccess?: EventEmitter<Action>
    ) {}
}

export class ConvertSubscriptionSuccess implements Action {
    readonly type = ActionTypes.ConvertSubscriptionSuccess;

    constructor(
        public readonly result: {
            previous: ClareServiceSubscription;
            current: ClareServiceSubscription;
            uuid: string;
            newServicePlan: ClareServicePlan;
            additionalFeatures?;
            isProjectOffline?: boolean;
        }
    ) {}
}

export type Actions =
    | ClearActiveSubscription
    | ClearSubscriptions
    | CreateSubscription
    | CreateSubscriptionFailure
    | CreateSubscriptionSuccess
    | GetActiveSubscriptionByProjectId
    | GetActiveSubscriptionByProjectIdFailure
    | GetActiveSubscriptionByProjectIdSuccess
    | UpdateSubscription
    | UpdateSubscriptionSuccess
    | UpdateSubscriptionFailure
    | ConvertSubscription
    | ConvertSubscriptionSuccess;
